import { Controller } from "@hotwired/stimulus";

export default class UIBulkSelectController extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "count",
    "records",
    "actionBar",
    "defaultAction",
    "pageSelected",
    "allSelected",
  ];

  connect() {
    this.selections = [];
    this.isSelectAll = false;
  }

  toggle({ target }) {
    const { type, id } = target.dataset;

    if (target.checked) {
      if (!this._isSelected(type, id)) {
        this.selections.push({ type, id });
      }
    } else {
      this.isSelectAll = false;
      this._removeSelection(type, id);
      this._uncheckSelectAll();
    }

    this._syncState();
  }

  toggleAll() {
    const checked = this._inputFor(this.selectAllTarget).checked;

    this.checkboxTargets.forEach((el) => {
      const input = this._inputFor(el);
      if (!input) return;

      input.checked = checked;
      const { type, id } = input.dataset;

      if (checked && !this._isSelected(type, id)) {
        this.selections.push({ type, id });
      } else if (!checked) {
        this._removeSelection(type, id);
      }
    });

    if (!checked) {
      this.isSelectAll = false;
    }

    this._syncState();
  }

  selectAll({ currentTarget }) {
    const type = currentTarget.dataset.type;
    this.isSelectAll = true;

    // The server's current folder is authoritative; the URL's folder_id can be stale (e.g. a global
    // search rendered from inside a folder), which would otherwise move the wrong folder's contents.
    const folderId = currentTarget.dataset.folderId || null;
    const payload = { type, select_all: true, folder_id: folderId };

    this._setRecordsValue(payload);
    this._showActionBar();
    this._updateCount();
    this._updateSelectAllBanner();
  }

  preventEmptySubmit(event) {
    if (this.selections.length === 0 && !this.isSelectAll) {
      event.preventDefault();
    }
  }

  clearSelection() {
    this.isSelectAll = false;
    this.selections = [];

    this.checkboxTargets.forEach((el) => {
      const input = this._inputFor(el);
      if (input) input.checked = false;
    });

    this._uncheckSelectAll();
    this._syncState();
    this._updateSelectAllBanner();
  }

  // Private

  _isSelected(type, id) {
    return this.selections.some((s) => s.type === type && s.id === id);
  }

  _removeSelection(type, id) {
    this.selections = this.selections.filter(
      (s) => !(s.type === type && s.id === id),
    );
  }

  _uncheckSelectAll() {
    if (!this.hasSelectAllTarget) return;
    const input = this._inputFor(this.selectAllTarget);
    if (input) input.checked = false;
  }

  _syncState() {
    if (this.isSelectAll) return;

    this._setRecordsValue(this.selections);

    if (this.selections.length > 0) {
      this._showActionBar();
    } else {
      this._hideActionBar();
    }

    this._updateCount();
    this._updateSelectAllBanner();
  }

  _setRecordsValue(value) {
    const json = JSON.stringify(value);
    this.recordsTargets.forEach((input) => {
      input.value = json;
    });
  }

  _showActionBar() {
    this.actionBarTargets.forEach((el) => el.classList.remove("hidden"));
    this.defaultActionTargets.forEach((el) => el.classList.add("hidden"));
  }

  _hideActionBar() {
    this.actionBarTargets.forEach((el) => el.classList.add("hidden"));
    this.defaultActionTargets.forEach((el) => el.classList.remove("hidden"));
  }

  _inputFor(el) {
    if (el.matches("input[type=checkbox]")) return el;
    return el.querySelector("input[type=checkbox]");
  }

  _updateSelectAllBanner() {
    if (this.hasPageSelectedTarget) {
      const allChecked =
        this.selections.length > 0 &&
        this.checkboxTargets.every((el) => {
          const input = this._inputFor(el);
          return input && input.checked;
        });
      this.pageSelectedTarget.classList.toggle(
        "hidden",
        !allChecked || this.isSelectAll,
      );
    }
    if (this.hasAllSelectedTarget) {
      this.allSelectedTarget.classList.toggle("hidden", !this.isSelectAll);
    }
  }

  _updateCount() {
    const text = this.isSelectAll ? "All" : this.selections.length;
    this.countTargets.forEach((el) => {
      el.textContent = text;
    });
  }
}
